<template>
  <div class="tw-my-2 tw-inline-flex tw-items-center">
    <SfButtonDs
      class="!tw-rounded-r-none hover:tw-z-10 focus-visible:tw-z-10"
      @click.prevent="toggleFollowing"
    >
      <span class="tw-flex tw-items-center tw-gap-2">
        <!-- The button owns the click, so the checkbox must not add a second
             one: its label forwards a click that bubbles, and it is focusable. -->
        <SfToggle
          :model-value="isCurrentUserFollowing"
          :icons="false"
          tabindex="-1"
          @click.prevent.stop
        />
        <span v-tooltip="tooltip" data-testid="follow-company-toggle--label">
          {{ label }}
        </span>
      </span>
    </SfButtonDs>

    <SfDropdown
      :dropdown-id="dropdownId"
      :is-open="isScopedDropdownOpen"
      variant="seamless"
      :unstyled-trigger="true"
      drawer-direction="left"
      @dropdown-clicked="handleToggleDropdown"
      @dropdown-trigger-clicked="handleToggleDropdown"
    >
      <template #trigger>
        <SfButtonDs
          v-tooltip="menuTooltip"
          icon-only
          icon-before="ellipsis-v"
          class="-tw-ml-px !tw-rounded-l-none"
          :aria-label="menuTooltip"
          data-testid="more-options-link"
        />
      </template>
      <template #menu>
        <SfDropdownItem v-if="followCount > 0" @click="openFileFollowersModal">
          {{ viewFollowersLabel }}
        </SfDropdownItem>
        <SfDropdownItem v-else :is-disabled="true" class="sf-c-light">
          {{ viewFollowersLabel }}
        </SfDropdownItem>
        <SfDropdownItem
          v-if="insightsEnabled"
          :is-wrapped="false"
          @click="openInsightsLink"
        >
          <div class="tw-flex tw-justify-start tw-gap-2 tw-w-full tw-min-h-0">
            <span class="tw-inline-flex tw-items-center tw-min-w-[14px]">
              <img
                class="tw-block"
                width="14"
                height="14"
                :src="$imagesRequire('icon-insights.svg')"
              />
            </span>
            <span>{{ insightsLinkText }}</span>
          </div>
        </SfDropdownItem>
      </template>
    </SfDropdown>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useDropdown } from '@app/shared/pinia/dropdown'
import { followCompany, unfollowCompany } from '@app/application/workflows/api'
import { SfButtonDs } from '@silverfin/ui-components'
import SfDropdown from '@app/shared/vue/connected/sf-dropdown.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'
import SfToggle from '@app/shared/vue/components/sf-toggle.vue'
import { SfLog } from '@app/adapters/logger'
import { comparisonModes, filters } from '@app/insights/constants'
import type { Follower } from '../types'
import { useCompanyFollowStore } from '../stores/use-company-follow-store'

const I18n = window.I18n

defineOptions({
  name: 'FollowCompanyToggle',
})

interface Props {
  insightsEnabled: boolean
  followers: Follower[]
  currentUser: Follower
}

const props = defineProps<Props>()
const dropdownStore = useDropdown()
const followStore = useCompanyFollowStore()
followStore.setInitialData(props.followers, props.currentUser)
const { followCount, isCurrentUserFollowing } = storeToRefs(followStore)
const { currentDropdownId } = storeToRefs(dropdownStore)
const dropdownId = 'follow-company-toggle'
const isScopedDropdownOpen = computed(() => {
  return currentDropdownId.value === dropdownId
})
function handleToggleDropdown() {
  dropdownStore.toggleDropdown(dropdownId)
}

const label = I18n.t('company.following.follow_client_file')
const tooltip = computed(() =>
  props.insightsEnabled
    ? I18n.t('company.following.follow_client_file_tooltip_with_insights')
    : I18n.t('company.following.follow_client_file_tooltip_without_insights')
)
const menuTooltip = I18n.t('company.following.more_options')
const filterParams = [
  {
    type: filters.FOLLOW_COMPANIES,
    params: {
      comparison_mode: comparisonModes.CURRENT_USER,
      comparison_value: '',
      current_user_id: props.currentUser.id,
    },
  },
]

const viewFollowersLabel = computed(() =>
  followCount.value > 0
    ? I18n.t('company.following.view_followers', {
        count: followCount.value,
      })
    : I18n.t('company.following.no_followers')
)

function openFileFollowersModal() {
  window.modalCitizen.openModalPortal('file-followers-modal')
  closeDropdown()
}

const insightsLinkText = I18n.t(
  'company.following.view_all_files_followed_by_me'
)

function openInsightsLink(): void {
  const insightsLink = window.Silverfin.Routes.companies_overview_index_path({
    firm_id: window.Silverfin.currentFirmId,
    filters: JSON.stringify(filterParams),
  })

  window.open(insightsLink, '_blank')?.focus()
  closeDropdown()
}

function closeDropdown() {
  dropdownStore.toggleDropdown(dropdownId)
}

async function toggleFollowing(): Promise<void> {
  try {
    if (isCurrentUserFollowing.value) {
      await unfollowCompany()
    } else {
      await followCompany()
    }

    isCurrentUserFollowing.value = !isCurrentUserFollowing.value
  } catch (error) {
    SfLog.error('Failed to toggle company following', {
      error: error,
    })
  }
}
</script>
